<template>
  <div>
    <label
      v-if="label"
      class="text-12 font-bold opacity-80 uppercase mb-2 text-gray-800"
      :class="labelClass"
      :id="uid"
    >
      {{ label }}
    </label>
    <div class="border-3 border-solid flex flex-row bg-white rounded-4 h-16" :class="wrapperClass">
      <input
        type="number"
        inputmode="numeric"
        :min="minValue"
        :step="minValue"
        class="py-1 px-3 flex-1 w-full min-w-0 border-r-3 border-solid border-gray-200 text-18 font-bold outline-none my-2 mx-0 placeholder:font-normal placeholder:text-gray-400"
        :aria-labelledby="uid"
        :value="amountValue || undefined"
        :placeholder="amountPlaceholder"
        :required="required"
        :autofocus="autofocus"
        @keydown="onKeydown"
        @input="onInput"
        @invalid="onInvalid"
      />
      <CurrencySelect
        v-if="currencies"
        class="border-none min-w-24 py-1 pr-1 pl-4 -ml-1"
        selection-class="py-3 pr-3 h-full"
        :currencies="currencies"
        :no-overlay="noOverlay"
        :show-flag="showCurrencyFlag"
        :header-text="headerText"
        :filter="currencyFilter"
        :model-value="currencyValue"
        @update:model-value="$emit('input-currency', $event)"
      />
      <span v-else class="font-bold p-3 flex items-center flex-nowrap">
        <span class="flag rounded-4" :class="`flag-${currencyValue.toLowerCase()}`" />
        {{ currencyValue }}
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Currency } from '../currency-select/types'

const uid = useId()

const props = withDefaults(
  defineProps<{
    currencies?: Currency[]
    label?: string
    labelClass?: string
    wrapperClass?: string
    amountValue: number
    amountPlaceholder?: string
    currencyValue: string
    customError?: string
    showCurrencyFlag?: boolean
    noOverlay?: boolean
    autofocus?: boolean
    required?: boolean
    allowDecimals?: boolean
    currencyFilter?: boolean
    headerText: string
  }>(),
  { currencyValue: '' },
)

const emit = defineEmits(['input-amount', 'input-currency'])

const { subscriptions } = useRuntimeConfig().public

const minValue = props.allowDecimals ? 0.000001 : 1

function onInvalid(event: Event) {
  if (props.customError) {
    const target = event.target as HTMLInputElement
    target.setCustomValidity(props.customError)
  }
}

function onKeydown(event: Event) {
  if ('key' in event) {
    if (event.key === 'e' || (!props.allowDecimals && event.key === '.')) {
      event.preventDefault()
    }
  }
}

function onInput(event: Event) {
  if (event.target) {
    const target = event.target as HTMLInputElement
    target.setCustomValidity('')
    target.reportValidity()

    if (target.value) {
      const value = target.value.replace(/[^0-9.]/g, '')
      let number = Number(Number.parseFloat(value).toFixed(props.allowDecimals ? 6 : 0))
      if (number > subscriptions.maxAmount) {
        number = subscriptions.maxAmount
      }
      target.value = Number.isNaN(number) ? '' : number.toString(10)
    }
    emit('input-amount', target.value)
  }
}
</script>
